/**
 * Mobile-first cyber snapshot block — extracted from the old MobileExecPage so
 * it can live inside the unified Executive Summary page. Renders well on a
 * phone (single column, big tap targets) and spreads wider on desktop.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Shield, AlertTriangle, Skull, Eye, Clock, Bug, ChevronRight, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface CyberSnap {
  threat_level: { level: string; score: number; updated_at?: string } | null
  diff: any | null
  patch_tuesday: { date: string; days_until: number } | null
  exposure: any | null
  top_critical: { cve_id: string; severity: string; cvss_score: number; vendors: string[]; products: string[] }[]
}

const LEVEL_TONE: Record<string, { bg: string; text: string; ring: string; label: string }> = {
  low:      { bg: 'bg-emerald/15', text: 'text-emerald',     ring: 'ring-emerald/40', label: 'LOW' },
  medium:   { bg: 'bg-amber/15',   text: 'text-amber-light', ring: 'ring-amber/40',   label: 'MEDIUM' },
  high:     { bg: 'bg-rose/15',    text: 'text-rose-light',  ring: 'ring-rose/40',    label: 'HIGH' },
  elevated: { bg: 'bg-rose/15',    text: 'text-rose-light',  ring: 'ring-rose/40',    label: 'ELEVATED' },
  critical: { bg: 'bg-rose/25',    text: 'text-rose',        ring: 'ring-rose/60',    label: 'CRITICAL' },
}

export default function CyberSnapshot() {
  const [data, setData] = useState<CyberSnap | null>(null)
  const [loading, setLoading] = useState(true)
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [threatRes, diffRes, ptRes, expRes, topRes] = await Promise.allSettled([
        api.get('/api/cyber/threat-level'),
        api.get('/api/cyber/diff', { params: { days: 7 } }),
        api.get('/api/cyber/patch-tuesday'),
        api.get('/api/cyber/exposure').catch(() => ({ data: null })),
        api.get('/api/cyber/top-risk', { params: { limit: 5, days: 14, sort: 'risk' } }),
      ])
      setData({
        threat_level:  threatRes.status === 'fulfilled' ? threatRes.value.data : null,
        diff:          diffRes.status   === 'fulfilled' ? diffRes.value.data   : null,
        patch_tuesday: ptRes.status     === 'fulfilled' ? ptRes.value.data     : null,
        exposure:      expRes.status    === 'fulfilled' ? expRes.value.data    : null,
        top_critical:  topRes.status    === 'fulfilled' ? (topRes.value.data?.top || []) : [],
      })
      setUpdatedAt(new Date())
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    fetchAll()
    const onVis = () => { if (!document.hidden) fetchAll() }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [fetchAll])

  const lvl = data?.threat_level?.level || 'medium'
  const tone = LEVEL_TONE[lvl] || LEVEL_TONE.medium
  const newKev    = data?.diff?.kev?.current ?? 0
  const newCvesC  = data?.diff?.cves?.current?.critical ?? 0
  const ransomCur = data?.diff?.ransomware?.victims_current ?? 0
  const exposed   = data?.exposure?.subdomains?.length ?? data?.exposure?.subdomains_total ?? 0

  return (
    <div className="space-y-3">
      {/* Threat level + 4 big-number tiles in one responsive row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Threat level hero — spans 2 cols on tablet, 1 on desktop */}
        <div className={clsx('rounded-xl p-4 ring-2 md:col-span-2 lg:col-span-1', tone.bg, tone.ring)}>
          <div className="text-[9px] uppercase tracking-widest text-text-muted">Threat level</div>
          <div className={clsx('text-3xl font-black mt-1 font-mono', tone.text)}>{tone.label}</div>
          <div className="text-[10px] text-text-secondary mt-1">
            score <span className="font-mono">{data?.threat_level?.score?.toFixed?.(1) ?? '—'}</span>
          </div>
          {data?.threat_level?.updated_at && (
            <div className="text-[9px] text-text-muted mt-0.5">
              {formatDistanceToNow(new Date(data.threat_level.updated_at), { addSuffix: true })}
            </div>
          )}
        </div>

        <BigTile to="/app/cyber"      icon={AlertTriangle} tone="rose"   value={newKev}    label="New KEV (7d)" />
        <BigTile to="/app/cyber"      icon={Shield}        tone="rose"   value={newCvesC}  label="Critical CVEs (7d)" />
        <BigTile to="/app/ransomware" icon={Skull}         tone="purple" value={ransomCur} label="Ransomware (7d)" />
        <BigTile to="/app/exposure"   icon={Eye}           tone="cyan"   value={exposed}   label="Subdomains" />
      </div>

      {/* Patch Tuesday + Top critical CVEs side-by-side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {data?.patch_tuesday && (
          <Link to="/app/countdowns"
            className="block rounded-xl p-4 bg-blue/10 border border-blue/30 hover:bg-blue/15 active:bg-blue/15">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-blue-light shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-blue-light">Patch Tuesday</div>
                <div className="text-base font-semibold text-text-primary truncate">
                  {data.patch_tuesday.days_until === 0 ? 'Today' : `T-${data.patch_tuesday.days_until} days`}
                </div>
                <div className="text-[10px] text-text-muted mt-0.5">{data.patch_tuesday.date}</div>
              </div>
              <ChevronRight size={16} className="text-text-muted" />
            </div>
          </Link>
        )}

        <div className="card p-3 lg:col-span-2">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-[11px] font-semibold text-text-primary uppercase tracking-wider">
              Top critical risks
            </h2>
            <Link to="/app/cyber" className="text-[10px] text-blue-light">all →</Link>
          </div>
          <div className="space-y-1.5">
            {(data?.top_critical || []).slice(0, 5).map(c => (
              <Link key={c.cve_id} to={`/app/cve/${encodeURIComponent(c.cve_id)}`}
                className="flex items-center gap-2 p-2 rounded-md bg-surface-raised hover:bg-surface-hover active:bg-surface-hover">
                <Bug size={14} className="text-rose shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] text-blue-light truncate">{c.cve_id}</div>
                  <div className="text-[10px] text-text-muted truncate">
                    {(c.vendors || []).slice(0, 2).join(', ') || '—'}
                    {c.products?.[0] ? ` · ${c.products[0]}` : ''}
                  </div>
                </div>
                <span className="text-[11px] font-mono font-bold text-rose-light shrink-0">
                  {c.cvss_score?.toFixed(1) ?? '—'}
                </span>
                <ChevronRight size={12} className="text-text-muted shrink-0" />
              </Link>
            ))}
            {(!data?.top_critical || data.top_critical.length === 0) && !loading && (
              <p className="text-[10px] text-text-muted italic text-center py-2">No critical CVEs in window.</p>
            )}
          </div>
        </div>
      </div>

      {/* Footer: refresh + last updated */}
      <div className="flex items-center justify-between px-1">
        <button type="button" onClick={fetchAll}
          className="flex items-center gap-1.5 text-[10px] text-text-muted hover:text-text-primary active:text-text-primary">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          Refresh snapshot
        </button>
        {updatedAt && (
          <span className="text-[10px] text-text-muted font-mono">{updatedAt.toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  )
}

function BigTile({ to, icon: Icon, tone, value, label }: {
  to: string
  icon: React.ElementType
  tone: 'rose' | 'amber' | 'cyan' | 'purple'
  value: number
  label: string
}) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'cyan' ? 'text-cyan' : 'text-purple-300'
  return (
    <Link to={to} className="rounded-xl p-3 bg-surface-card border border-border hover:bg-surface-hover active:bg-surface-hover">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={t} />
        <div className="text-[9px] uppercase tracking-widest text-text-muted">{label}</div>
      </div>
      <div className={clsx('text-3xl font-black font-mono', t)}>{(value || 0).toLocaleString()}</div>
    </Link>
  )
}
